import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { cloneDeep, isEmpty, keys, noop } from 'lodash';
// Import Store
import { useAppDispatch, useAppSelector } from 'store';
import { deleteDynamicSegmentFiltersItem, selectDynamicSegmentFiltersData } from 'store/slices/dynamicSegment';
// Import UI components
import { Icon, Tooltip } from '@geneui/components';
// Import Components
import ConfirmationModal from 'components/common/Modals/ConfirmationModal';
// Import Containers
import { UIHashComparisonContainer } from '../UIHashContainers';
// Import Services
import { UtilsHttpService } from 'services/http';
import { getFiltersElementView, getFiltersAddDataView } from './service';
// Import Helpers
import { ignoreCanceledError } from 'services/Helpers';
// Import Constants
import { l, ModalsClassNames, ActionsIcons, SEGMENT_FILTER_APPLICATION_COLUMN_ID } from 'constants/common';
// Import Hooks
import useCancelableRequests from 'hooks/UseCancelableRequests';
import { usePartnerDateFormats } from 'hooks/UsePartnerDateFormats';

const FilterGroupItem = (props) => {
    const {
        item,
        index,
        withoutActions,
        handleChangeActiveList,
        resetActiveList,
        setReadOnly,
        readOnly,
        editingFilter,
        setEditingFilter,
        additionalData,
        tabActiveIndex,
    } = props;
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const dynamicSegmentFiltersData = useAppSelector(selectDynamicSegmentFiltersData);

    const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);
    const [updatedAdditionalData, setUpdateAdditionalData] = useState<Nullable<AnyObject>>(null);

    const { getMenuItems, getDataById } = useCancelableRequests({
        getMenuItems: UtilsHttpService.getMenuItems,
        getDataById: UtilsHttpService.getDataById,
    });

    const dateFormats = usePartnerDateFormats();

    const filtersGroupItemMemo = useMemo(() => {
        const filtersElementView = getFiltersElementView(t, item, updatedAdditionalData || additionalData, dateFormats);

        return (
            <div
                className={classNames('crm-filters-group-item', {
                    'crm-editable-item': item === editingFilter && readOnly,
                })}
            >
                <ConfirmationModal
                    isVisibleModal={isOpenDeleteModal}
                    onOk={handelDeleteItem}
                    onCancel={setDeleteModalOpen}
                    questionLabel={t(l.DoYouWantToDeleteThisFilterItemFromDynamicSegmentFilterContainer)}
                    titleText={t(l.DeleteFilterFromDynamicSegmentFilterContainer)}
                    iconType={ActionsIcons.Delete}
                    actionLabel={t(l.Delete)}
                    className={ModalsClassNames.Delete}
                    withPortal={true}
                />

                <Tooltip text={filtersElementView} position="bottom">
                    <p>{filtersElementView}</p>
                </Tooltip>

                {!withoutActions && (
                    <div className="crm-filters-group-item__icons">
                        <Icon
                            type={`${item === editingFilter && readOnly ? 'bc-icon-cancel-bonus' : 'bc-icon-edit'} ${
                                item === editingFilter ? 'is-editing-filter' : ''
                            }`}
                            disabled={item !== editingFilter && readOnly}
                            onClick={handleEditItem}
                        />
                        <Icon
                            type="bc-icon-trash"
                            onClick={setDeleteModalOpen}
                            disabled={item !== editingFilter && readOnly}
                        />
                    </div>
                )}
            </div>
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        additionalData,
        updatedAdditionalData,
        item?.comparison?.colorUI,
        item?.comparison?.uiType,
        item?.comparison?.comparison,
        item,
        readOnly,
        editingFilter,
        isOpenDeleteModal,
    ]);

    useEffect(() => {
        const { comparison, Value, viewType } = item ?? {};

        if (withoutActions || !additionalData) {
            const subSegmentIds = Object.entries(dynamicSegmentFiltersData)
                .filter(([tabIndex]) => tabIndex !== `${tabActiveIndex}`)
                .map(([, { id }]) => id);

            const filteredAddData = getFiltersAddDataView(
                comparison,
                Value,
                viewType,
                getMenuItems,
                getDataById,
                t,
                dynamicSegmentFiltersData,
                subSegmentIds,
            );
            if (filteredAddData instanceof Promise) {
                if (comparison.elementType === 1) {
                    filteredAddData
                        .then((res) => {
                            const data = res.Data[0] as AnyObject;
                            if (JSON.stringify(data) !== JSON.stringify(additionalData)) {
                                item?.setAdditionalData?.({ value: data.Id, label: data.Name });
                                setUpdateAdditionalData({ value: data.Id, label: data.Name });
                            }
                        })
                        .catch(ignoreCanceledError);
                } else {
                    filteredAddData
                        .then((data) => {
                            if (JSON.stringify(data) !== JSON.stringify(additionalData)) {
                                const resultAddData = data.map((el) => {
                                    return {
                                        value: el.Id,
                                        label: el.Name,
                                    };
                                });
                                item?.setAdditionalData?.(resultAddData);
                                setUpdateAdditionalData(resultAddData);
                            }
                        })
                        .catch(ignoreCanceledError);
                }
            } else {
                item?.setAdditionalData?.(filteredAddData);
                setUpdateAdditionalData(filteredAddData);
            }
        } else {
            item?.setAdditionalData?.(additionalData);
            setUpdateAdditionalData(additionalData);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [additionalData, item, dynamicSegmentFiltersData]);

    useEffect(() => {
        if (
            item?.comparison?.applicationColumnId === SEGMENT_FILTER_APPLICATION_COLUMN_ID &&
            !isEmpty(dynamicSegmentFiltersData) &&
            additionalData
        ) {
            const tmpAdditionalData = cloneDeep(item?.additionalData);
            tmpAdditionalData.forEach((item) => {
                const value = item?.dataValue ?? item?.value;
                if (keys(dynamicSegmentFiltersData).includes(value) && item) {
                    item.label = dynamicSegmentFiltersData[value].name;
                }
            });
            setUpdateAdditionalData(tmpAdditionalData);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tabActiveIndex, dynamicSegmentFiltersData]);

    function setDeleteModalOpen() {
        setIsOpenDeleteModal(!isOpenDeleteModal);
    }

    function handelDeleteItem() {
        const { parentHashKey } = item;
        dispatch(deleteDynamicSegmentFiltersItem({ parentHashKey, index, id: tabActiveIndex }));
        setDeleteModalOpen();
        if (readOnly) {
            resetActiveList();
            setEditingFilter(null);
        }
    }

    function handleEditItem() {
        const { hashKey, displayName } = item?.comparison ?? {};
        setReadOnly(true);
        if (item !== editingFilter) {
            setEditingFilter(item);
            handleChangeActiveList(hashKey, displayName, index);
        } else {
            resetActiveList();
            setEditingFilter(null);
        }
    }

    return <>{filtersGroupItemMemo}</>;
};

FilterGroupItem.propTypes = {
    item: PropTypes.instanceOf(UIHashComparisonContainer),
    index: PropTypes.number,
    withoutActions: PropTypes.bool,
    readOnly: PropTypes.bool,
    handleChangeActiveList: PropTypes.func,
    resetActiveList: PropTypes.func,
    setReadOnly: PropTypes.func,
    setEditingFilter: PropTypes.func,
    editingFilter: PropTypes.object,
    additionalData: PropTypes.any,
    tabActiveIndex: PropTypes.number.isRequired,
};

FilterGroupItem.defaultProps = {
    withoutActions: false,
    editingFilter: null,
    resetActiveList: noop,
    setEditingFilter: noop,
    readOnly: false,
    setReadOnly: noop,
    handleChangeActiveList: noop,
};

export default FilterGroupItem;
